/* ==========================================================================
   bb-cart.js — Beyond Bohemian "Collection Notes" cart drawer behaviors.

   Everything here is a custom element, because the theme replaces the whole
   .drawer__inner over AJAX on every cart change (see assets/cart.js,
   CartDrawerItems.getSectionsToRender). Custom elements re-run their
   connectedCallback after each innerHTML swap, so every behavior below
   self-initializes on every re-render with zero external wiring. This is
   the documented fix for the 2026-08-24 inline-script failure.

   2026-09-03: Companion Row now lifts server-rendered rows (with spec line,
   stars, Quick Shop and open ladders) from sections/bb-cart-companion-rows.
   ========================================================================== */
(function () {
  'use strict';

  var money = function (cents) {
    try {
      var cur = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD';
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: cur }).format(cents / 100);
    } catch (e) {
      return '$' + (cents / 100).toFixed(2);
    }
  };

  /* Replace the drawer contents from a fresh server render, then let the
     rest of the page know. Mirrors the theme's own section swap. */
  function refreshDrawer(cartJson) {
    return fetch(window.location.pathname + '?sections=cart-drawer')
      .then(function (r) { return r.json(); })
      .then(function (sections) {
        var html = sections['cart-drawer'];
        if (!html) return;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fresh = doc.querySelector('.drawer__inner');
        var current = document.querySelector('#CartDrawer .drawer__inner');
        if (fresh && current) current.innerHTML = fresh.innerHTML;
        if (cartJson && typeof cartJson.item_count === 'number') {
          document.querySelectorAll('.cart-icon-bubble-count').forEach(function (el) {
            el.textContent = cartJson.item_count;
          });
          var drawerEl = document.querySelector('cart-drawer');
          if (drawerEl) drawerEl.classList.toggle('is-empty', cartJson.item_count === 0);
        }
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: 'bb-cart' } }));
      })
      .catch(function (e) { console.error('[bb-cart] drawer refresh failed', e); });
  }

  function addToCart(variantId) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: 1 })
    }).then(function (r) {
      if (!r.ok) throw new Error('add.js ' + r.status);
      return fetch('/cart.js').then(function (c) { return c.json(); });
    });
  }

  /* ---------- delivery estimate: today + N..M business days ---------- */
  if (!customElements.get('bb-cart-eta')) {
    customElements.define('bb-cart-eta', class extends HTMLElement {
      connectedCallback() {
        try {
          var min = parseInt(this.dataset.min || '3', 10);
          var max = parseInt(this.dataset.max || '7', 10);
          var add = function (n) {
            var d = new Date();
            while (n > 0) {
              d.setDate(d.getDate() + 1);
              var day = d.getDay();
              if (day !== 0 && day !== 6) n--;
            }
            return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
          };
          this.textContent = add(min) + ' to ' + add(max);
        } catch (e) { /* fallback text stays */ }
      }
    });
  }

  /* ---------- rotating review: 10s cycle, dots, pause on hover ---------- */
  if (!customElements.get('bb-cart-review')) {
    customElements.define('bb-cart-review', class extends HTMLElement {
      connectedCallback() {
        var self = this;
        var slides = self.querySelectorAll('.bbc-rev__slide');
        if (!slides.length) return;
        var dotsWrap = self.querySelector('.bbc-rev__dots');
        var idx = 0, paused = false;
        if (self._timer) clearInterval(self._timer);
        if (dotsWrap) {
          dotsWrap.innerHTML = '';
          slides.forEach(function (s, i) {
            var d = document.createElement('button');
            d.type = 'button';
            d.setAttribute('aria-label', 'Review ' + (i + 1));
            d.setAttribute('aria-pressed', String(i === 0));
            d.addEventListener('click', function () { show(i); });
            dotsWrap.appendChild(d);
          });
        }
        var dots = dotsWrap ? dotsWrap.querySelectorAll('button') : [];
        function show(i) {
          idx = i;
          slides.forEach(function (s, j) { s.classList.toggle('bbc-on', j === i); });
          dots.forEach(function (d, j) { d.setAttribute('aria-pressed', String(j === i)); });
        }
        show(0);
        self.addEventListener('mouseenter', function () { paused = true; });
        self.addEventListener('mouseleave', function () { paused = false; });
        var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (slides.length > 1 && !reduce) {
          self._timer = setInterval(function () { if (!paused) show((idx + 1) % slides.length); }, 10000);
        }
      }
      disconnectedCallback() { if (this._timer) clearInterval(this._timer); }
    });
  }

  /* ---------- companion row: server-rendered rows + open size ladders ----------
     2026-09-03: rows come from sections/bb-cart-companion-rows.liquid via the
     recommendations Section Rendering endpoint, so each carries grade | size
     | origin, stars, a Quick Shop button and its pills. Order of attempts:
       product 1 complementary → related → product 2 … → same-shelves fallback
     Results are cached per bag composition so qty changes never refetch. */
  /* 2026-09-09: Massy's loader boots async. Measured on the live store,
     window.Massy.getPrice only exists several seconds after DOMContentLoaded,
     which is AFTER the drawer paints its rows. The first version returned
     silently when Massy was missing and never retried, so a wholesale buyer saw
     retail every time. Poll for up to 12s, then give up quietly and leave retail. */
  function bbWhenMassyReady(cb, tries) {
    tries = (typeof tries === 'number') ? tries : 48;
    if (window.Massy && typeof window.Massy.getPrice === 'function') { cb(); return; }
    if (tries <= 0) return;
    setTimeout(function () { bbWhenMassyReady(cb, tries - 1); }, 250);
  }

  /* 2026-09-10: THE reason every reprice below looked like it did nothing.
     Massy's tiers are SPEND bands, not quantity bands. In its own rules payload
     they are stored as quantityFrom/quantityTo = 150 / 300 / 1000, and
     Massy.getPrice(variantId, N) passes N straight into applyPricingRule, which
     picks a band with `quantityFrom <= N`. We were passing 1. 150 <= 1 is false,
     so no band ever matched, no discount was ever applied, and getPrice returned
     the retail price to every caller, for every customer, at every bag size.
     Verified 2026-09-10 by reading Massy's own bundle (745-7b32c25a.js,
     applyPricingRule / getTotalQuantity) and by calling getPrice on the live
     storefront: a $45.00 variant returns 45, i.e. SHOP-CURRENCY DOLLARS.
     The correct second argument is the gross bag total in shop currency, which
     is what Massy's getTotalQuantity computes for an ENTIRE_ORDER spend rule:
     the sum of line prices BEFORE any discount, including lines that are
     themselves excluded from the discount. original_line_price is presentment,
     so divide by Shopify.currency.rate to get back to shop currency. */
  var bbGrossCache = { at: 0, p: null };
  function bbGrossBagDollars() {
    var now = Date.now();
    if (bbGrossCache.p && (now - bbGrossCache.at) < 1500) return bbGrossCache.p;
    bbGrossCache.at = now;
    bbGrossCache.p = fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (c) {
        var rate = parseFloat((window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) || '1') || 1;
        if (!(rate > 0)) rate = 1;
        var cents = (c.items || []).reduce(function (t, it) {
          return t + (typeof it.original_line_price === 'number' ? it.original_line_price : (it.price || 0) * (it.quantity || 0));
        }, 0);
        return cents / 100 / rate;
      })
      .catch(function () { return 0; });
    return bbGrossCache.p;
  }

  var compCache = {};
  if (!customElements.get('bb-cart-companion')) {
    customElements.define('bb-cart-companion', class extends HTMLElement {
      connectedCallback() {
        var self = this;
        var ids = (self.dataset.productIds || '').split(',').filter(Boolean);
        var rowsHost = self.querySelector('[data-bbc-rows]');
        if (!ids.length || !rowsHost) return;
        var section = self.dataset.section || 'bb-cart-companion-rows';
        var base = (window.routes && window.routes.product_recommendations_url) || '/recommendations/products';
        var cacheKey = section + '|' + (self.dataset.cartKey || ids.join(','));

        function paint(html) {
          rowsHost.innerHTML = html;
          self.classList.add('bbc-ready');
          wirePills();
          bbWhenMassyReady(repriceForWholesale);
        }
        /* 2026-09-09: the rows print retail (variant.price in Liquid). For a
           wholesale buyer whose bag has already reached a tier, ask Massy's own
           engine for the unit price and show it, retail struck through. Three
           guards, all of which must hold or the row stays retail:
             1. the server rendered the wholesale strip (customer present + tagged),
             2. the strip says a tier is active ("You're at N% off"), so an empty or
                sub-$150 bag is never marked down (the 2026-09-06 failure),
             3. Massy.getPrice returned a number strictly below retail.
           Massy.getPrice is the same engine that prices the bag, so this cannot
           disagree with checkout the way a parsed widget did. */
        function repriceForWholesale() {
          var strip = document.getElementById('bb-wholesale-strip');
          if (!strip || !window.Massy || typeof window.Massy.getPrice !== 'function') return;
          if (!/You're at \d+% off/.test(strip.textContent || '')) return;
          bbGrossBagDollars().then(function (gross) {
          if (!(gross > 0)) return;
          var note = self.querySelector('.bbc-comp__ws-note');
          var anyRepriced = false;
          rowsHost.querySelectorAll('[data-bbc-row]').forEach(function (row) {
            var pills = row.querySelectorAll('.bbc-pill[data-variant]');
            var lows = [];
            var jobs = [];
            pills.forEach(function (pill) {
              var b = pill.querySelector('b');
              if (!b) return;
              var retail = parseFloat((b.textContent || '').replace(/[^0-9.]/g, ''));
              if (!retail) return;
              jobs.push(Promise.resolve(window.Massy.getPrice(pill.dataset.variant, gross)).then(function (p) {
                var n = Number(p);
                if (!isFinite(n) || n <= 0 || n >= retail) return;
                lows.push(n);
                anyRepriced = true;
                b.innerHTML = money(Math.round(n * 100)) + ' <s style="opacity:.55;font-weight:400">' + money(Math.round(retail * 100)) + '</s>';
                pill.classList.add('bbc-pill--ws');
              }).catch(function () {}));
            });
            Promise.all(jobs).then(function () {
              var from = row.querySelector('.bbc-comp__from');
              if (from && lows.length) {
                var min = Math.min.apply(null, lows);
                from.textContent = (pills.length > 1 ? 'From ' : '') + money(Math.round(min * 100));
              }
              if (note && anyRepriced) note.textContent = 'Prices shown at your current wholesale rate.';
            });
          });
          });
        }
        function wirePills() {
          rowsHost.querySelectorAll('.bbc-pill').forEach(function (pill) {
            pill.addEventListener('click', function () {
              if (pill.disabled) return;
              var original = pill.innerHTML;
              pill.disabled = true;
              pill.classList.add('bbc-pill--added');
              pill.innerHTML = '&#10003; Added';
              addToCart(pill.dataset.variant)
                .then(function (cart) { return refreshDrawer(cart); })
                .catch(function (e) {
                  console.error('[bb-cart] companion add failed', e);
                  pill.disabled = false;
                  pill.classList.remove('bbc-pill--added');
                  pill.innerHTML = original;
                });
            });
          });
        }
        function fetchBlocks(pid, intent) {
          var url = base + '?section_id=' + encodeURIComponent(section) + '&product_id=' + pid + '&limit=8&intent=' + intent;
          return fetch(url)
            .then(function (r) { return r.ok ? r.text() : ''; })
            .then(function (html) {
              var doc = new DOMParser().parseFromString(html || '', 'text/html');
              var primary = doc.querySelector('[data-bbc-primary]');
              var fallback = doc.querySelector('[data-bbc-fallback]');
              return {
                primary: primary && primary.querySelector('[data-bbc-row]') ? primary.innerHTML : '',
                fallback: fallback && fallback.querySelector('[data-bbc-row]') ? fallback.innerHTML : ''
              };
            })
            .catch(function () { return { primary: '', fallback: '' }; });
        }

        if (compCache[cacheKey]) { paint(compCache[cacheKey]); return; }

        var attempts = [];
        ids.slice(0, 3).forEach(function (pid) {
          attempts.push([pid, 'complementary']);
          attempts.push([pid, 'related']);
        });
        var fallbackHtml = '';
        var run = function (i) {
          if (i >= attempts.length) {
            if (fallbackHtml) { compCache[cacheKey] = fallbackHtml; paint(fallbackHtml); }
            else self.style.display = 'none';
            return;
          }
          fetchBlocks(attempts[i][0], attempts[i][1]).then(function (b) {
            if (!self.isConnected) return;
            if (b.primary) { compCache[cacheKey] = b.primary; paint(b.primary); return; }
            if (b.fallback && !fallbackHtml) fallbackHtml = b.fallback;
            run(i + 1);
          });
        };
        run(0);
      }
    });
  }

  /* ---------- quick shop shell dedupe ----------
     The bag ships its own bb-quickshop shell (snippets/cart-drawer.liquid) for
     pages without a product grid. When a grid already rendered one, drop the
     bag's copy so bb-quickshop.js and Massy only ever see a single root. */
  function dedupeQuickShopShell() {
    var shells = document.querySelectorAll('[data-bb-qs]');
    if (shells.length < 2) return;
    shells.forEach(function (s) { if (s.hasAttribute('data-bb-qs-cart')) s.parentNode.removeChild(s); });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', dedupeQuickShopShell);
  else dedupeQuickShopShell();

  /* ---------- gift watcher: server-computed state, self-healing ---------- */
  /* 2026-09-09 re-price after sign-in. A cart built while the storefront session
     was anonymous keeps its retail allocations until the next cart mutation, so a
     wholesale buyer who signs in sees retail lines and a wholesale rail at once.
     If the server rendered the wholesale strip (customer present + tagged) and the
     bag holds lines with no discount allocation above the first tier, fire ONE
     no-op attribute write, which makes Shopify re-run the discount function, then
     re-render the drawer. Once per session. Never on the add-to-cart path: that
     race was tested on 2026-09-05 and does not exist. */
  (function () {
    if (!document.getElementById('bb-wholesale-strip')) return;
    var KEY = 'bb-ws-repriced';
    try { if (sessionStorage.getItem(KEY) === '1') return; } catch (e) {}
    fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        if (!cart || !cart.items || !cart.items.length) return;
        var gross = 0, bare = 0;
        cart.items.forEach(function (it) {
          if (it.handle === 'a-piece-from-the-collection') return;
          gross += it.original_line_price || 0;
          if (!(it.line_level_discount_allocations && it.line_level_discount_allocations.length)) bare++;
        });
        if (gross < 15000 || bare === 0) return;
        try { sessionStorage.setItem(KEY, '1'); } catch (e) {}
        return fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ attributes: { bb_ws_repriced: String(Date.now()) } })
        }).then(function (r) { return r.json(); }).then(function (c) { return refreshDrawer(c); });
      })
      .catch(function (e) { console.error('[bb-cart] wholesale re-price failed', e); });
  })();

  if (!customElements.get('bb-gift-watcher')) {
    customElements.define('bb-gift-watcher', class extends HTMLElement {
      connectedCallback() {
        var self = this;
        if (window.__bbGiftBusy) return;
        var variant = self.dataset.variant;
        if (!variant) return;
        var eligible = self.dataset.eligible === 'true';
        var hasGift = self.dataset.hasGift === 'true';
        var giftLine = parseInt(self.dataset.giftLine || '0', 10);
        var giftQty = parseInt(self.dataset.giftQty || '0', 10);
        /* 2026-09-09: one gift per bag, ever. The gift variant is $0.00, so extra
           units cost nothing and ship real inventory. Steady state below only asks
           whether a gift is present, never how many, so cap it here. Scoped to
           giftLine, which is the gift's own cart line, so no other item is touched. */
        if (eligible && hasGift && giftLine > 0 && giftQty > 1 && !window.__bbGiftCapped) {
          if (window.__bbGiftBusy) return;
          /* one attempt per page load: if the server somehow still reports >1 after
             the re-render, stop rather than loop the cart endpoint */
          window.__bbGiftCapped = true;
          window.__bbGiftBusy = true;
          fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ line: giftLine, quantity: 1 })
          }).then(function (r) { return r.json(); })
            .then(function (cart) { return refreshDrawer(cart); })
            .then(function () { window.__bbGiftBusy = false; })
            .catch(function (e) { console.error('[bb-cart] gift qty cap failed', e); window.__bbGiftBusy = false; });
          return;
        }
        if (eligible === hasGift) return; /* steady state */
        window.__bbGiftBusy = true;
        var done = function () { window.__bbGiftBusy = false; };
        if (eligible && !hasGift) {
          addToCart(variant).then(function (cart) { return refreshDrawer(cart); }).then(done)
            .catch(function (e) { console.error('[bb-cart] gift add failed (is the gift product published to the Online Store channel?)', e); done(); });
        } else if (!eligible && hasGift && giftLine > 0) {
          fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ line: giftLine, quantity: 0 })
          }).then(function (r) { return r.json(); })
            .then(function (cart) { return refreshDrawer(cart); }).then(done)
            .catch(function (e) { console.error('[bb-cart] gift remove failed', e); done(); });
        } else {
          done();
        }
      }
    });
  }
})();
